import React from 'react';
import { CheckCircleIcon, WarningCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import type { EligibilityReasonRow, EligibilityStatus } from '../../../../services/financeEligibilityService';

function StatusGlyph({ status }: { status: EligibilityStatus }) {
    if (status === 'success') {
        return (
            <CheckCircleIcon
                size={16}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--success"
                aria-label="Eligible"
            />
        );
    }
    if (status === 'failed') {
        return (
            <XCircleIcon
                size={16}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--failed"
                aria-label="Not eligible"
            />
        );
    }
    return (
        <WarningCircleIcon
            size={16}
            weight="fill"
            className="fp-eligibility__icon fp-eligibility__icon--warning"
            aria-label="Review"
        />
    );
}

function statusText(status: EligibilityStatus): string {
    if (status === 'success') return 'Eligible';
    if (status === 'failed') return 'Not eligible';
    return 'Review';
}

function groupReasonRows(rows: EligibilityReasonRow[]): Array<{ title: string; rows: EligibilityReasonRow[] }> {
    const groups: Array<{ title: string; rows: EligibilityReasonRow[] }> = [];
    for (const row of rows) {
        const title = row.group || row.label || 'Eligibility';
        const last = groups[groups.length - 1];
        if (last && last.title === title) {
            last.rows.push(row);
        } else {
            groups.push({ title, rows: [row] });
        }
    }
    return groups;
}

export function EligibilityReasonTable({
    heading,
    rows,
}: {
    heading: string;
    rows: EligibilityReasonRow[];
}) {
    const groups = groupReasonRows(rows);
    const showCriteria = rows.some((row) => Boolean(row.label && row.group));
    return (
        <div className="fp-eligibility__info-tooltip fp-eligibility__info-tooltip--table">
            <div className="fp-eligibility__info-heading">{heading}</div>
            {groups.length ? (
                <table className="fp-eligibility__reason-table">
                    <thead>
                        <tr>
                            <th>Status</th>
                            {showCriteria ? <th>Criteria</th> : null}
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => (
                            <React.Fragment key={group.title}>
                                <tr className="fp-eligibility__reason-table__group">
                                    <td colSpan={showCriteria ? 3 : 2}>{group.title}</td>
                                </tr>
                                {group.rows.map((row) => (
                                    <tr key={row.key}>
                                        <td>
                                            <span className="fp-eligibility__reason-table__status">
                                                <StatusGlyph status={row.status} />
                                                <span className={`fp-eligibility__overall-label fp-eligibility__overall-label--${row.status}`}>
                                                    {statusText(row.status)}
                                                </span>
                                            </span>
                                        </td>
                                        {showCriteria ? <td>{row.label}</td> : null}
                                        <td className="fp-eligibility__reason-table__reason">{row.reason}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="fp-eligibility__info-notes">No criteria details available.</div>
            )}
        </div>
    );
}
